/**
 * 投放项目、任意章节选择、共享营销内容及后台执行的跨端契约。
 * 项目和版本均为 admin 独有资源；原书正文和旧任务身份不变。
 */
import { z } from 'zod';
import type { LandingAiReviewResult } from './landing-ai-config';
import type { LandingTextChange } from './landing-desensitization-log';
import {
  LandingCopySchema,
  LandingTaskAssetSchema,
  LandingTemplateIdSchema,
} from './landing-pages';
import type { LandingCopy, LandingPageDto, LandingTaskAsset } from './landing-pages';
import type { NovelVisibility } from './novel-visibility';
import { LanguageCodeSchema } from './system-config';

export const LANDING_PROJECT_STATUSES = ['ACTIVE', 'ARCHIVED'] as const;
/**
 * 项目列表按已加入投放章节的 AI 脱敏状态筛选（AE §11.1.2），与章节 desensitization_status 同口径：
 * NOT_PROCESSED / FAILED 为至少一章命中，SUCCEEDED 为已加入投放章节全部成功（不含零章节项目）。
 */
export const LANDING_PROJECT_DESENSITIZATION_FILTERS = [
  'NOT_PROCESSED',
  'FAILED',
  'SUCCEEDED',
] as const;
/** 「一键脱敏」纳入的 AI 脱敏状态：未处理或失败；处理中、已成功、待重新脱敏不纳入（ADM-LP-S010）。 */
export function isQuickDesensitizeStatus(
  status: LandingProjectChapterDto['desensitization_status'],
) {
  return status === 'NOT_PROCESSED' || status === 'FAILED';
}
/**
 * 「一键脱敏」可处理的已投放章节（ADM-LP-S010 / AE §11.1.2 chapters.desensitizable）：状态未处理或失败，
 * 且本章与按章号相邻的前后投放章原章均可用 —— 提交时相邻投放章作为参考正文，任一失效即整批拒收。
 * 入参为项目全部已投放章节；服务端计数与前端弹窗选章共用本判定。
 */
export function quickDesensitizableChapters<
  T extends Pick<
    LandingProjectChapterDto,
    'chapter_index' | 'selectable' | 'desensitization_status'
  >,
>(chapters: readonly T[]): T[] {
  const ordered = [...chapters].sort((a, b) => a.chapter_index - b.chapter_index);
  return ordered.filter(
    (chapter, index) =>
      isQuickDesensitizeStatus(chapter.desensitization_status) &&
      chapter.selectable &&
      ordered[index - 1]?.selectable !== false &&
      ordered[index + 1]?.selectable !== false,
  );
}
/** 运营手动投放标记，与发布独立（AE §11.4.1）。 */
export const LANDING_PAGE_DELIVERY_STATUSES = ['NOT_DELIVERING', 'DELIVERING'] as const;
export type LandingPageDeliveryStatus = (typeof LANDING_PAGE_DELIVERY_STATUSES)[number];
export const LANDING_PROJECT_EXECUTION_STATUSES = [
  'PENDING',
  'RUNNING',
  'SUCCEEDED',
  'PARTIAL',
  'FAILED',
  'CANCELLED',
] as const;
export const LANDING_PROJECT_ITEM_STATUSES = [
  'PENDING',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'CANCELLED',
  'REUSED',
] as const;
export type LandingProjectOperation = 'review' | 'desensitize' | 'generate' | 'publish';
const ChapterIdsSchema = z
  .array(z.uuid())
  .min(1)
  .max(1000)
  .refine((ids) => new Set(ids).size === ids.length, '章节不能重复');
const TaskIdsSchema = z
  .array(z.uuid())
  .min(1)
  .max(1000)
  .refine((ids) => new Set(ids).size === ids.length, '任务不能重复');
export const LandingProjectListQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(20),
    search: z.string().max(256).optional(),
    status: z.enum(LANDING_PROJECT_STATUSES).optional(),
    product_id: z.uuid().optional(),
    /** 书籍语言版本（项目所选 novels.locale），canonical language_code，单值精确匹配。 */
    language: LanguageCodeSchema.optional(),
    sort: z.enum(['updated_desc', 'created_desc', 'name_asc']).default('updated_desc'),
    availability: z.enum(['free', 'paid', 'all']).default('all'),
    desensitization_status: z.enum(LANDING_PROJECT_DESENSITIZATION_FILTERS).optional(),
  })
  .strict();
/**
 * 项目 TAG：拼进 AppLovin 创意组名 `TAG-{value}` 段（dev-docs `02-API契约/85-归因与深链.md` §1.2，
 * 运营自定义、服务端不解析）。字符集取创意组名允许集去掉 `_`（`_` 是段分隔符）；
 * 32 位上限给 BOOK/BY/OS/LANG/APP/UTMSOURCE 留够空间，整串仍 ≤ 100。
 */
export const LandingProjectTagSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9.-]{1,32}$/, 'TAG 只能包含英文字母、数字、点和短横线，最多 32 位');
export const CreateLandingProjectRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(128),
    note: z.string().max(2000).nullable().optional(),
    tag: LandingProjectTagSchema.nullable().optional(),
    novel_id: z.uuid(),
    product_id: z.uuid(),
    media_source: z.string().min(1).max(256),
    chapter_ids: ChapterIdsSchema.optional(),
    template_id: LandingTemplateIdSchema.nullable().optional(),
    show_topbar: z.boolean().optional(),
    copy_overrides: LandingCopySchema.nullable().optional(),
    // 兼容旧客户端请求；服务端固定关闭脱敏后复审。
    post_review_enabled: z.boolean().optional(),
  })
  .strict();
export const UpdateLandingProjectRequestSchema = CreateLandingProjectRequestSchema.pick({
  name: true,
  note: true,
  tag: true,
  template_id: true,
  show_topbar: true,
  copy_overrides: true,
  post_review_enabled: true,
})
  .partial()
  .extend({
    status: z.enum(LANDING_PROJECT_STATUSES).optional(),
    refresh_recipe: z.boolean().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, '至少提供一个修改字段');
export const CopyLandingProjectRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(128).optional(),
    product_id: z.uuid().optional(),
    media_source: z.string().min(1).max(256).optional(),
  })
  .strict();
export const ImportLegacyLandingProjectRequestSchema = z
  .object({
    task_ids: TaskIdsSchema,
    name: z.string().trim().min(1).max(128).optional(),
  })
  .strict();
export type ImportLegacyLandingProjectRequest = z.infer<
  typeof ImportLegacyLandingProjectRequestSchema
>;
export const ReplaceLandingProjectChaptersRequestSchema = z
  .object({ chapter_ids: ChapterIdsSchema })
  .strict();
export const StartLandingProjectContentRequestSchema = z
  .object({
    operation: z.enum(['review', 'desensitize']),
    chapter_ids: ChapterIdsSchema.optional(),
    force: z.boolean().optional(),
  })
  .strict();
export const StartLandingProjectExecutionRequestSchema = z
  .object({
    operation: z.enum(['generate', 'publish']),
    task_ids: TaskIdsSchema.optional(),
    retry_failed_only: z.boolean().optional(),
  })
  .strict();
const LandingProjectAssetFields = {
  name: z.string().trim().min(1).max(255),
  asset: LandingTaskAssetSchema,
  width: z.number().int().positive().max(50000).optional(),
  height: z.number().int().positive().max(50000).optional(),
  bytes: z
    .number()
    .int()
    .positive()
    .max(2 * 1024 * 1024)
    .optional(),
  content_type: z.enum(['image/webp', 'image/png', 'image/jpeg']).optional(),
};
export const CreateLandingProjectAssetRequestSchema = z.object(LandingProjectAssetFields).strict();
export const UpdateLandingProjectAssetRequestSchema = z
  .object(LandingProjectAssetFields)
  .partial()
  .strict()
  .refine((value) => Object.keys(value).length > 0, '至少提供一个修改字段');
export const UpdateLandingProjectTaskDeliveryRequestSchema = z
  .object({ delivery_status: z.enum(LANDING_PAGE_DELIVERY_STATUSES) })
  .strict();
export type UpdateLandingProjectTaskDeliveryRequest = z.infer<
  typeof UpdateLandingProjectTaskDeliveryRequestSchema
>;
export const AdoptLandingChapterVersionRequestSchema = z
  .object({ content_version_id: z.uuid(), confirm_content_reviewed: z.boolean().optional() })
  .strict();
export const RejectLandingChapterVersionRequestSchema = z
  .object({
    expected_content_version_id: z.uuid(),
    reason: z.string().trim().min(1).max(1000),
  })
  .strict();
export const SaveLandingProjectChapterContentRequestSchema = z
  .object({
    content: z
      .string()
      .min(1)
      .max(5 * 1024 * 1024),
    expected_content_version_id: z.uuid().nullable(),
    expected_source_sha256: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict();
export type SaveLandingProjectChapterContentRequest = z.infer<
  typeof SaveLandingProjectChapterContentRequestSchema
>;

export type CreateLandingProjectRequest = z.infer<typeof CreateLandingProjectRequestSchema>;
export type UpdateLandingProjectRequest = z.infer<typeof UpdateLandingProjectRequestSchema>;
export type CopyLandingProjectRequest = z.infer<typeof CopyLandingProjectRequestSchema>;
export type CreateLandingProjectAssetRequest = z.infer<
  typeof CreateLandingProjectAssetRequestSchema
>;
export type UpdateLandingProjectAssetRequest = z.infer<
  typeof UpdateLandingProjectAssetRequestSchema
>;
export type StartLandingProjectContentRequest = z.infer<
  typeof StartLandingProjectContentRequestSchema
>;
export type StartLandingProjectExecutionRequest = z.infer<
  typeof StartLandingProjectExecutionRequestSchema
>;

export interface LandingProjectExecutionItemDto {
  id: string;
  /** 冻结的章节号/标题或素材名称，运营无需辨认数据库 UUID。 */
  label?: string;
  task_id: string | null;
  chapter_id: string | null;
  status: (typeof LANDING_PROJECT_ITEM_STATUSES)[number];
  phase: string;
  completed: number;
  total: number;
  error: string | null;
  shared_execution_id: string | null;
  result: Record<string, unknown> | null;
  started_at: string | null;
  heartbeat_at: string | null;
  finished_at: string | null;
  ai_usage?: import('./landing-ai-usage').LandingAiUsageSummary;
  model_calls?: import('./landing-ai-usage').LandingAiModelCallDto[];
}
export interface LandingProjectExecutionDto {
  id: string;
  project_id: string;
  operation: LandingProjectOperation;
  status: (typeof LANDING_PROJECT_EXECUTION_STATUSES)[number];
  phase: string;
  completed: number;
  total: number;
  succeeded: number;
  failed: number;
  reused: number;
  error: string | null;
  can_retry: boolean;
  created_at: string;
  started_at: string | null;
  heartbeat_at: string | null;
  finished_at: string | null;
  items: LandingProjectExecutionItemDto[];
  ai_usage?: import('./landing-ai-usage').LandingAiUsageSummary;
}
/** 执行队列总览范围：active = PENDING/RUNNING，finished = 已结束四态，all = 全部。 */
export const LANDING_PROJECT_EXECUTION_QUEUE_STATES = ['active', 'finished', 'all'] as const;
export type LandingProjectExecutionQueueState =
  (typeof LANDING_PROJECT_EXECUTION_QUEUE_STATES)[number];
export const LandingProjectExecutionQueueQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(20),
    state: z.enum(LANDING_PROJECT_EXECUTION_QUEUE_STATES).default('active'),
    operation: z.enum(['review', 'desensitize', 'generate', 'publish']).optional(),
  })
  .strict();
export type LandingProjectExecutionQueueQuery = z.infer<
  typeof LandingProjectExecutionQueueQuerySchema
>;
/** 跨项目执行队列行（dev-docs AE §11.5.1）：执行 DTO 去掉子项，补项目、书籍、提交人与模型排队信息。 */
export interface LandingProjectQueueExecutionDto extends Omit<LandingProjectExecutionDto, 'items'> {
  project: {
    id: string;
    code: string;
    name: string;
    status: (typeof LANDING_PROJECT_STATUSES)[number];
  };
  novel: { book_code: string; locale: string; title: string };
  created_by: { id: string; nickname: string | null };
  /** 最早一条未完成共享章节处理当前阶段的模型服务商；生成/发布或已结束为 null。 */
  provider: string | null;
  /** 同服务商排在本批前面的未完成共享章节处理数；只表示查询时刻，不承诺位次或时长。 */
  queue_ahead: number | null;
}
/** 项目所选书在目标 App 中的非公开状态；不改变生成资格。 */
export interface LandingBookVisibilityWarning {
  app_id: string;
  app_name: string;
  visibility: Exclude<NovelVisibility, 'PUBLIC'>;
}
export interface LandingProjectBookVisibilityWarningDto extends LandingBookVisibilityWarning {
  project_id: string;
  project_code: string;
  project_name: string;
  project_status: (typeof LANDING_PROJECT_STATUSES)[number];
  novel_id: string;
  book_code: string;
  book_title: string;
  cn_title: string | null;
  locale: string;
  product_name: string;
}
export const LandingProjectBookVisibilityWarningsQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(20),
  })
  .strict();
export interface LandingProjectDto {
  /** 旧响应可缺省；新服务端返回全部目标 App 中当前非公开的状态。 */
  book_visibility_warnings?: LandingBookVisibilityWarning[];
  can_generate: boolean;
  generation_blocked_reason: string | null;
  id: string;
  code: string;
  name: string;
  note: string | null;
  /** 运营自定义 TAG，拼创意组名用；null = 未设置 */
  tag: string | null;
  status: (typeof LANDING_PROJECT_STATUSES)[number];
  novel: {
    id: string;
    book_code: string;
    book_group_code: string | null;
    locale: string;
    title: string;
    cn_title: string | null;
    cover_url: string | null;
    source_external_id: string | null;
  };
  product: { id: string; name: string; brand: string };
  media_source: string;
  template_id: string | null;
  config_version: number;
  chapter_ids: string[];
  show_topbar: boolean;
  copy_overrides: LandingCopy | null;
  /** @deprecated 脱敏后复审已移除，固定 false。 */
  post_review_enabled: boolean;
  recipe_sha256: string | null;
  chapters: {
    selected: number;
    ready: number;
    /** 与章节列表有效终态一致；缺省时旧响应不推断为正文就绪数。 */
    reviewed?: number;
    desensitized?: number;
    /** 一键脱敏可处理章数：原章可用且 AI 脱敏未处理 / 失败（AE §11.1.2）；旧响应缺省时不推断。 */
    desensitizable?: number;
    /** 当前 HTML 正文门槛未通过的章节；旧响应可缺省，不能从 AI 成功量推断就绪。 */
    blocked?: { chapter_id: string; chapter_index: number; title: string; reason: string }[];
    reused: number;
    failed: number;
  };
  media_count: number;
  tasks: { total: number; generated: number; stale: number; failed: number };
  active_execution: LandingProjectExecutionDto | null;
  error_summary: string | null;
  /** 项目创建人；账号记录不存在时 nickname 为 null（软删账号仍返回昵称）。旧响应可缺省。 */
  created_by?: { id: string; nickname: string | null };
  created_at: string;
  updated_at: string;
}
export interface LandingProjectChapterDto {
  id: string;
  is_free: boolean;
  cost: string;
  chapter_index: number;
  title: string;
  published_at: string | null;
  selectable: boolean;
  unavailable_reason: string | null;
  selected: boolean;
  source_version: number;
  source_sha256: string;
  content_version_id: string | null;
  available_content_version_id: string | null;
  content_sha256: string | null;
  rule_status: 'NOT_PROCESSED' | 'SUCCEEDED' | 'UNCHANGED';
  review_status: 'NOT_REVIEWED' | 'PENDING' | 'RUNNING' | 'PASSED' | 'ISSUES' | 'FAILED' | 'STALE';
  desensitization_status:
    | 'NOT_PROCESSED'
    | 'PENDING'
    | 'RUNNING'
    | 'SUCCEEDED'
    | 'UNCHANGED'
    | 'FAILED'
    | 'STALE';
  /** @deprecated 脱敏后复审已移除，固定 NOT_REVIEWED。 */
  recheck_status: 'NOT_REVIEWED' | 'PENDING' | 'RUNNING' | 'PASSED' | 'ISSUES' | 'FAILED' | 'STALE';
  review_result: LandingAiReviewResult | null;
  /** @deprecated 脱敏后复审已移除，固定 null；历史记录不改写。 */
  recheck_result: LandingAiReviewResult | null;
  desensitization_content_version_id: string | null;
  /** 脱敏详情结果是否位于当前采用链；缺省兼容旧响应，无结果时为 null。 */
  desensitization_result_adopted?: boolean | null;
  /** 自动采用的阻断原因；执行完成不等于正文效果已通过。 */
  adoption_block_reason?: string | null;
  /** v3 成功即采用后，规则无法确认项的抽查提示（不阻断采用与 HTML 生成）；其余为 null。 */
  adoption_hint?: string | null;
  reused: boolean;
  /** 已采用版本的原书来源（正文哈希 / 快照版本 / 标题 / 格式）与当前源章节不一致。 */
  is_source_stale: boolean;
  /** 已采用版本的处理方案（模型 / 提示词 / 词库）与项目最近捕获的方案不一致；原文可能未变。 */
  is_recipe_stale: boolean;
  error: string | null;
  execution: LandingProjectExecutionDto | null;
}
export interface LandingProjectAssetDto {
  id: string;
  project_id: string;
  name: string;
  asset: LandingTaskAsset;
  task_id: string | null;
  width: number | null;
  height: number | null;
  bytes: number | null;
  content_type: string | null;
  content_sha256: string | null;
  /** 关联落地页摘要；素材未绑定任务时为 null，字段语义同 LandingProjectTaskDto。 */
  task: LandingProjectAssetTaskSummaryDto | null;
  created_at: string;
  updated_at: string;
}
export interface LandingProjectAssetTaskSummaryDto {
  id: string;
  /** 下载短码，不是数据库 UUID。 */
  task_id: string;
  generate_status: LandingPageDto['generate_status'];
  stale_reason: string | null;
  delivery_status: LandingPageDeliveryStatus;
  delivery_outdated: boolean;
  download_count: number;
}
export interface LandingProjectTaskDto extends LandingPageDto {
  project_id: string;
  asset_id: string | null;
  asset_name: string | null;
  selected_chapter_ids: string[];
  config_version: number;
  artifact_version: string | null;
  input_fingerprint: string | null;
  generated_input_fingerprint: string | null;
  stale_reason: string | null;
  can_download: boolean;
  download_block_reason: string | null;
  needs_redownload: boolean;
  delivery_status: LandingPageDeliveryStatus;
  delivery_updated_at: string | null;
  /** 投放中且投放时的产物版本不是当前 artifact_version。 */
  delivery_outdated: boolean;
  /** 当前产物版本成功导出次数；新产物从 0 开始。 */
  download_count: number;
  last_downloaded_at: string | null;
  /** 最近一次成功生成 HTML 的完成时间；从未成功生成或旧响应为 null / 缺省。 */
  generated_at?: string | null;
  /** 提交最近一次成功生成的运营；账号记录不存在时 nickname 为 null（软删账号仍返回昵称）。 */
  generated_by?: { id: string; nickname: string | null } | null;
  execution: LandingProjectExecutionDto | null;
}
export interface LandingProjectChangeDto {
  html_inputs_changed: boolean;
  affected_task_ids: string[];
  affected_task_count: number;
  reason: string;
}
export interface LandingProjectMutationDto {
  project: LandingProjectDto;
  change: LandingProjectChangeDto;
  asset?: LandingProjectAssetDto;
}
export interface LandingChapterVersionDto {
  id: string;
  processing_type: 'SOURCE' | 'RULE' | 'AI' | 'MANUAL';
  status: string;
  source_sha256: string;
  content_sha256: string;
  recipe_sha256: string;
  provider: string | null;
  model: string | null;
  review_result: LandingAiReviewResult | null;
  is_current: boolean;
  /** 当前项目下的人工采用资格；缺省时客户端不能推定可采用。 */
  can_adopt?: boolean;
  adopt_block_reason?: string | null;
  adoption_block_reason?: string | null;
  is_rejected?: boolean;
  error: string | null;
  created_at: string;
}
export interface LandingChapterVersionDetailDto {
  log: LandingChapterVersionDto;
  changes: LandingTextChange[];
  diff_available: boolean;
}
export interface LandingProjectChapterContentDto {
  content: string;
  content_version_id: string | null;
  source_sha256: string;
  content_sha256: string;
  content_format: string;
  source_stale: boolean;
}
